import { t as uiText } from '../../i18n';
import React from 'react';
import { marked, type Token, type Tokens } from 'marked';

const LEX_OPTIONS = { gfm: true, breaks: true } as const;

/**
 * AI は `1.**見出し**` のように、リスト記号の直後に太字を置くことがある。
 * CommonMark は記号のあとに空白が必要なので、描画前にだけ補う。
 */
export function normalizeAgentMarkdown(text: string): string {
  return text.replace(/^(\s*(?:\d+[.)]|[-*+]))(\*\*)/gm, '$1 $2');
}

function isSafeHref(href: string): boolean {
  return /^(https?:|mailto:|tel:)/i.test(href.trim());
}

function headingTag(depth: number): 'h1' | 'h2' | 'h3' | 'h4' {
  if (depth <= 1) return 'h1';
  if (depth === 2) return 'h2';
  if (depth === 3) return 'h3';
  return 'h4';
}

function renderInline(tokens: Token[] | undefined, keyPrefix: string): React.ReactNode {
  if (!tokens || tokens.length === 0) return null;
  return tokens.map((token, index) => renderToken(token, `${keyPrefix}-${index}`));
}

function renderToken(token: Token, key: string): React.ReactNode {
  switch (token.type) {
    case 'space':
      return null;
    case 'heading':
      return React.createElement(
        headingTag(token.depth),
        { key, className: `agent-md__h agent-md__h--${Math.min(token.depth, 4)}` },
        renderInline(token.tokens, key),
      );
    case 'paragraph':
      return <p key={key}>{renderInline(token.tokens, key)}</p>;
    case 'text': {
      const textToken = token as Tokens.Text;
      if (textToken.tokens?.length) {
        return <React.Fragment key={key}>{renderInline(textToken.tokens, key)}</React.Fragment>;
      }
      return <React.Fragment key={key}>{textToken.text}</React.Fragment>;
    }
    case 'strong':
      return <strong key={key}>{renderInline(token.tokens, key)}</strong>;
    case 'em':
      return <em key={key}>{renderInline(token.tokens, key)}</em>;
    case 'del':
      return <del key={key}>{renderInline(token.tokens, key)}</del>;
    case 'codespan':
      return <code key={key}>{token.text}</code>;
    case 'code':
      return (
        <pre key={key}>
          <code>{token.text}</code>
        </pre>
      );
    case 'blockquote':
      return <blockquote key={key}>{renderInline(token.tokens, key)}</blockquote>;
    case 'list':
      return renderList(token as Tokens.List, key);
    case 'list_item':
      return (
        <li key={key}>
          {token.task ? (
            <input
              type="checkbox"
              className="agent-md__task"
              disabled
              checked={Boolean(token.checked)}
              readOnly
            />
          ) : null}
          {renderInline(token.tokens, key)}
        </li>
      );
    case 'link':
      return renderLink(token as Tokens.Link, key);
    case 'image':
      return renderImage(token as Tokens.Image, key);
    case 'br':
      return <br key={key} />;
    case 'hr':
      return <hr key={key} />;
    case 'escape':
      return <React.Fragment key={key}>{token.text}</React.Fragment>;
    case 'html':
      return <React.Fragment key={key}>{token.text}</React.Fragment>;
    case 'table':
      return renderTable(token as Tokens.Table, key);
    case 'def':
      return null;
    default: {
      const generic = token as Tokens.Generic;
      if (Array.isArray(generic.tokens) && generic.tokens.length > 0) {
        return <React.Fragment key={key}>{renderInline(generic.tokens, key)}</React.Fragment>;
      }
      if (typeof generic.text === 'string') {
        return <React.Fragment key={key}>{generic.text}</React.Fragment>;
      }
      return null;
    }
  }
}

function renderList(token: Tokens.List, key: string): React.ReactNode {
  const Tag = token.ordered ? 'ol' : 'ul';
  const start = token.ordered && typeof token.start === 'number' && token.start !== 1
    ? token.start
    : undefined;
  return (
    <Tag key={key} start={start}>
      {token.items.map((item, index) => renderToken(item, `${key}-i${index}`))}
    </Tag>
  );
}

function renderLink(token: Tokens.Link, key: string): React.ReactNode {
  const label = renderInline(token.tokens, key) ?? token.text;
  if (!isSafeHref(token.href)) {
    return <span key={key}>{label}</span>;
  }
  const isTel = token.href.startsWith('tel:');
  return (
    <a
      key={key}
      href={token.href}
      title={token.title || undefined}
      target={isTel ? undefined : '_blank'}
      rel={isTel ? undefined : 'noopener noreferrer'}
      aria-label={isTel
        ? uiText('Call: {{label}}', { label: token.text })
        : uiText('External link: {{label}}', { label: token.text })}
    >
      {label}
    </a>
  );
}

function renderImage(token: Tokens.Image, key: string): React.ReactNode {
  if (!/^https?:/i.test(token.href.trim())) return null;
  return (
    <img
      key={key}
      className="agent-md__img"
      src={token.href}
      alt={token.text || uiText('Image')}
      title={token.title || undefined}
      loading="lazy"
    />
  );
}

function renderTable(token: Tokens.Table, key: string): React.ReactNode {
  return (
    <table key={key}>
      <thead>
        <tr>
          {token.header.map((cell, index) => (
            <th key={`${key}-h${index}`} style={alignStyle(cell.align)}>
              {renderInline(cell.tokens, `${key}-h${index}`)}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {token.rows.map((row, rowIndex) => (
          <tr key={`${key}-r${rowIndex}`}>
            {row.map((cell, cellIndex) => (
              <td key={`${key}-r${rowIndex}c${cellIndex}`} style={alignStyle(cell.align)}>
                {renderInline(cell.tokens, `${key}-r${rowIndex}c${cellIndex}`)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function alignStyle(align: Tokens.TableCell['align']): React.CSSProperties | undefined {
  if (!align) return undefined;
  return { textAlign: align };
}

export function renderAgentMarkdown(text: string): React.ReactNode {
  if (!text) return null;
  const tokens = marked.lexer(normalizeAgentMarkdown(text), LEX_OPTIONS);
  return <>{tokens.map((token, index) => renderToken(token, `md-${index}`))}</>;
}
